<script setup>
import { ref, onBeforeMount } from 'vue'

import HeroSection from '@/components/sections/HeroSection.vue'
import ServicesSection from '@/components/sections/ServicesSection.vue'
import CaseSection from '@/components/sections/CaseSection.vue'
import TeamSection from '@/components/sections/TeamSection.vue'
import ThanksSection from '@/components/sections/ThanksSection.vue'
import ContactSection from '@/components/sections/ContactSection.vue'

import { getMain } from '@/api/page-main.js'
import { getCases, getCasesCategory } from '@/api/cases.js'

const services = ref([])
const gratitudes = ref([])
const employees = ref([])
const contacts = ref([])

async function loadMain() {
	try {
		const response = await getMain()
		const data = response.data

		services.value = processServices(data.services)
		gratitudes.value = processGratitudes(data.gratitudes)
		employees.value = processEmployees(data.employees)
		contacts.value = processContacts(data.contacts)
	} catch (error) {
		console.error('Ошибка загрузки данных', error)
		notifyUser('Не удалось загрузить данные. Пожалуйста, попробуйте позже.')
		resetData()
	}
}

const cases = ref([])
const caseCategories = ref([])
const activeCategorySlug = ref(null)
const isLoading = ref(false)

async function loadCases(categorySlug = null, rawCategories = null) {
	try {
		isLoading.value = true
		const response = await getCases(categorySlug)
		const rawCases = response.data
		const categories = rawCategories || caseCategories.value
		cases.value = processCases(rawCases, categories, categorySlug)
	} catch (error) {
		console.error('Ошибка загрузки кейсов:', error.message)
		notifyUser('Не удалось загрузить кейсы')
		resetData()
	} finally {
		isLoading.value = false
	}
}

async function loadCategories() {
	try {
		const response = await getCasesCategory()
		const rawCategories = response.data
		caseCategories.value = processCaseCategories(rawCategories)
		return rawCategories
	} catch (error) {
		console.error('Ошибка загрузки категорий:', error.message)
		notifyUser('Не удалось загрузить категории')
		return []
	}
}

function processServices(items) {
	if (!Array.isArray(items)) return []
	return (items || []).map((item) => ({
		id: item.id,
		title: item.heading,
		description: item.description,
	}))
}

function processGratitudes(items) {
	if (!Array.isArray(items)) return []
	return (items || []).map((item) => ({
		id: item.id,
		image: item.image,
	}))
}

function processEmployees(items) {
	if (!Array.isArray(items)) return []
	return (items || []).map((item) => ({
		id: item.id,
		name: item.name,
		job_title: item.job_title,
		image_desktop: item.image_desktop,
		image_mobile: item.image_mobile,
	}))
}

function processContacts(contacts) {
	if (!contacts || typeof contacts !== 'object') return []
	return Object.entries(contacts).map(([key, value]) => ({
		type: key,
		value: value,
	}))
}

function processCases(items, categories, currentCategorySlug = null) {
	if (!Array.isArray(items)) return []

	return items.map((item) => {
		const category = categories.find((cat) => cat.id === item.category_id)
		return {
			id: item.id,
			title: item.heading,
			description: item.text,
			client: item.client,
			task: item.task,
			realized: item.realized,
			awards: item.awards,
			participants: item.participants,
			kpi: item.kpi,
			image_main: item.image_main,
			image_detail: item.image_detail,
			image_logo: item.image_logo,
			category_id: item.category_id ?? null,
			category_slug: category?.slug || item.category_slug || currentCategorySlug || 'all',
		}
	})
}

function processCaseCategories(items) {
	if (!Array.isArray(items)) return []
	return items.map((item) => ({
		id: item.id,
		name: item.name,
		slug: item.slug || item.name.toLowerCase().replace(/\s+/g, '-'),
	}))
}

async function onCategoryClick(slug) {
	if (activeCategorySlug.value === slug) {
		activeCategorySlug.value = null
		await loadCases()
	} else {
		activeCategorySlug.value = slug
		await loadCases(slug)
	}
}

function resetData() {
	services.value = []
	gratitudes.value = []
	employees.value = []
	contacts.value = []
	cases.value = []
	caseCategories.value = []
	activeCategorySlug.value = null
}

function notifyUser(message) {
	alert(message)
}

onBeforeMount(async () => {
	loadMain()
	const categories = await loadCategories()
	await loadCases(null, categories)
})
</script>

<template>
	<HeroSection />
	<ServicesSection :items="services" />
	<CaseSection
		:items="{ cases: cases, categories: caseCategories }"
		:activeCategorySlug="activeCategorySlug"
		@category-click="onCategoryClick"
		:loading="isLoading"
	/>
	<TeamSection :items="employees" />
	<ThanksSection :items="gratitudes" />
	<ContactSection :items="contacts" />
</template>
